import { epochDate, ISOString, Resource } from 'idea-toolbox';
import { Countries } from './countries.enum';
import { FieldsOfStudy } from './fieldsOfStudy.enum';

/**
 * Represent an User of the Application
 *
 * Table: `jupiter_users`.
 *
 * _Note_: the partion key isn't `sectionId` because the `userId` has to be unique (whole table).
 *
 * Indexes:
 *    - `sectionId-kind-index`.
 */
export class User extends Resource {
  userId: string;
  sectionId: string;
  fullName: string;
  email: string;
  createdAt: ISOString; //ISO 8601 format
  info: ProfileInfo;
  roles: Array<string>;
  exchangeInfo: ExchangeInfo;
  contacts: Contacts;
  residence: Address;
  domicile: Address;
  document: Document;
  emergencyContact: EmergencyContact;
  ESNcards: Array<ESNCard>;
  notes: string;
  flagged: boolean; //Flagged (highlighted in the UI) for some reason.
  memberships: Array<Membership>;
  /** Audience category used by event and product eligibility rules. */
  kind: UserKind;

  public load(x: any) {
    super.load(x);
    this.userId = this.clean(x.userId, String);
    this.sectionId = this.clean(x.sectionId, String);
    this.email = this.clean(x.email, String);
    this.createdAt = this.clean(x.createdAt, String);
    this.info = new ProfileInfo(x.info);
    this.fullName = this.info.firstName + ' ' + this.info.lastName;
    this.roles = this.cleanArray(x.roles, String);
    this.exchangeInfo = new ExchangeInfo(x.exchangeInfo);
    this.contacts = new Contacts(x.contacts);
    this.residence = new Address(x.residence);
    this.domicile = new Address(x.domicile);
    this.document = new Document(x.document);
    this.emergencyContact = new EmergencyContact(x.emergencyContact);
    this.ESNcards = this.cleanArray(x.ESNcards, (i: any) => new ESNCard(i));
    this.notes = this.clean(x.notes, String);
    this.flagged = this.clean(x.flagged, Boolean);
    this.memberships = this.cleanArray(x.memberships, (i: any) => new Membership(i));
    this.kind = this.clean(x.kind, String);
  }

  public safeLoad(newData: any, safeData: any) {
    super.safeLoad(newData, safeData);
    this.userId = safeData.userId;
    this.sectionId = safeData.sectionId;
    this.createdAt = safeData.createdAt;
    this.email = safeData.email;
    this.roles = safeData.roles;
    this.memberships = safeData.memberships;
    this.ESNcards = safeData.ESNcards;
    this.kind = safeData.kind;
  }

  public validate(): Array<string> {
    super.validate();
    const e: Array<string> = [];
    // bio
    if (this.iE(this.sectionId)) e.push(`sectionId`);
    if (!Object.values(UserKind).includes(this.kind)) e.push(`kind`);
    if (this.iE(this.fullName)) e.push(`fullName`);
    if (this.iE(this.email, 'email')) e.push(`email`);
    if (this.iE(this.createdAt)) e.push(`createdAt`);
    // profile
    e.push(...this.info.validate());
    // exchange
    e.push(...this.exchangeInfo.validate());
    // contacts
    e.push(...this.contacts.validate());
    // residence
    e.push(...this.residence.validate());
    // document
    e.push(...this.document.validate());
    // emergency contact
    e.push(...this.emergencyContact.validate());
    // esncard
    e.push(...this.ESNcards.reduce((a, c) => a.concat(c.validate()), []));
    return e;
  }

  // public isEventResponsible(event: Event): boolean {
  //   return (
  //     (event.owner && event.owner.userId == this.userId) ||
  //     (event.responsibles && event.responsibles.some(x => x.userId == this.userId))
  //   );
  // }

  // public canManageEvent(event: Event, sectionPermission?: boolean): boolean {
  //   if (sectionPermission) return this.permissions.canManageEvents;

  //   return (
  //     this.permissions.canManageEvents ||
  //     (event.owner && event.owner.userId == this.userId) ||
  //     (event.responsibles && event.responsibles.some(x => x.userId == this.userId))
  //   );
  // }

  // public canManageNationalEvents(nationalEvent: NationalEvent): boolean {
  //   if (!nationalEvent) return this.permissions.canAddNationalEvents;

  //   return (
  //     (this.permissions.canAddNationalEvents && nationalEvent.sectionId === this.sectionId) || // if user can manage National Events  OR
  //     (nationalEvent.createdInfo.executedBy && nationalEvent.createdInfo.executedBy.userId == this.userId) || // if user has created or
  //     (nationalEvent.admins.length > 0 && nationalEvent.admins.some((x: OCMember) => x.userId == this.userId)) // if user is one of the admins
  //   );
  // }

  // public canSeeNationalEvents(nationalEvent: NationalEvent): boolean {
  //   if (!nationalEvent) return false;

  //   return (
  //     !nationalEvent.isDraft &&
  //     nationalEvent.visibleTo.some((k: string) => k == this.kind) &&
  //     (nationalEvent.invitedSectionsInfo.some((i: InvitedSectionInfo) => i.sectionId == this.sectionId) ||
  //       this.sectionId == nationalEvent.sectionId)
  //   );
  // }

  // public isEventSectionResponsible(nationalEvent: NationalEvent): boolean {
  //   return (
  //     this.permissions.canManageEvents ||
  //     nationalEvent.owner.userId === this.userId ||
  //     (nationalEvent.responsibles && nationalEvent.responsibles.some((x: UserShort) => x.userId == this.userId))
  //   );
  // }

  // public isOCMember(event: NationalEvent): boolean {
  //   if (!event) return true;

  //   return (
  //     (event?.sectionId === this.sectionId && this.permissions.canAddNationalEvents) ||
  //     event?.owner.userId === this.userId ||
  //     event?.admins.find(admin => admin.userId === this.userId) != undefined
  //   );
  // }

  // public hasOCApplicated(event: NationalEvent) {
  //   return event.reqAdmins.find(reqAdmin => reqAdmin.userId === this.userId) != undefined;
  // }

  // public canApplyOC(event: NationalEvent): boolean {
  //   return [UserKind.ESNER, UserKind.ALUMNO].find(kind => kind === this.kind) != undefined && !this.isOCMember(event);
  // }

  // public isUserKindAllowed(event: Event): boolean {
  //   return (
  //     this.canManageEvent(event) || (!event.isDraft && !event.isEnded() && event?.visibleTo?.some(k => k === this.kind))
  //   );
  // }

  // public isErasmus(): boolean {
  //   return this.kind === UserKind.ERASMUS;
  // }

  // /**
  //  * Check whether the ESNcard is expired or not.
  //  */
  // public isESNcardExpired(): boolean {
  //   if (!this.ESNcard || !this.ESNcard.number) return false;
  //   let expDate = new Date(this.ESNcard.issuedAt);
  //   expDate.setFullYear(expDate.getFullYear() + 1);

  //   return Date.now() > expDate.getTime();
  // }

  // /**
  //  * Check whether the ESNcard is expiring or not. The card is expiring behind one month
  //  */
  // public isESNcardExpiring(): boolean {
  //   if (!this.ESNcard || !this.ESNcard.number) return false;
  //   let almostExpDate = new Date(this.ESNcard.issuedAt);
  //   let almostExpDate1 = new Date(this.ESNcard.issuedAt);
  //   almostExpDate.setFullYear(almostExpDate.getFullYear() + 1);
  //   almostExpDate.setMonth(almostExpDate.getMonth() - 1);
  //   almostExpDate1.setFullYear(almostExpDate1.getFullYear() + 1);
  //   if (Date.now() < almostExpDate1.getTime() && Date.now() > almostExpDate.getTime())
  //     return Date.now() > almostExpDate.getTime();
  // }

  // /**
  //  * Check whether the ESNcard is registered or not.
  //  */
  // public isESNcardNotRegistered(): boolean {
  //   if (!this.ESNcard || !this.ESNcard.number) return true;
  //   else return false;
  // }

  // /**
  //  * Whether if the user can administrate the section or not.
  //  */
  // canAdmin(): boolean {
  //   return this.permissions.canAdminSection;
  // }

  // /**
  //  * Whether if the user can edit other users or not.
  //  */
  // canEdit(): boolean {
  //   return this.permissions.canPutUsers;
  // }

  // /**
  //  * Whether if the user can list other users or not.
  //  */
  // canSee(): boolean {
  //   return this.permissions.canGetUsers;
  // }
  // /**
  //  *
  //  * @returns whether the user has paid the membership fee ever
  //  */
  // hasMembership(): boolean {
  //   const now = new Date();

  //   return this.membershipPaymentDate !== null && this.membershipPaymentDate <= now.valueOf();
  // }

  // /**
  //  *
  //  * @returns whether the user has paid the membership fee ever
  //  */
  // hasValidMembership(section: Section): boolean {
  //   if (!this.membershipPaymentDate) return false;

  //   return this.membershipPaymentDate >= section.legalInfo.membershipYearStartDate;
  // }

  /**
   * Export a dictionary of <label: data> User's information.
   */
  // public export(section: Section): ExternalUser {
  //   const ret: any = {
  //     'First Name': this.profileInfo.firstName,
  //     'Last Name': this.profileInfo.lastName,

  //     Birthdate: new Date(this.profileInfo.birthDate).toLocaleDateString('it-IT'),
  //     Birthplace: this.profileInfo.birthPlace,
  //     Country: this.profileInfo.country,
  //     Nationality: this.profileInfo.nationality,
  //     Email: this.contacts.email,
  //     Phone: this.contacts.phone,
  //     WhatsApp: this.contacts.whatsapp,
  //     originUniversity: this.originUniversity,
  //     originUniversityCity: this.originUniversityCity,
  //     exchangeUniversity: this.exchangeUniversity,
  //     fieldOfStudy: this.fieldOfStudy ?? '',
  //     'Creation Date': new Date(this.createdAt).toLocaleDateString('it-IT'),
  //     'Residence Address': this.residence.address,
  //     'Residence City': this.residence.city,
  //     'Residence Country': this.residence.country,
  //     'Domicile Address': this.domicile.address,
  //     'Domicile City': this.domicile.city,
  //     'Domicile Country': this.domicile.country,
  //     'Document Type': this.document.type,
  //     'Document Number': this.document.number,
  //     'Document Expiration Date': new Date(this.document.expDate).toLocaleDateString('it-IT'),
  //     'Document Issued In': this.document.issuedIn,
  //     'ESNCard Number': this.ESNcard.number,
  //     'ESNCard Issue Date': this.ESNcard.issuedAt ? new Date(this.ESNcard.issuedAt).toLocaleDateString('it-IT') : ''
  //   };

  //   Object.keys(section.customBlockFields.sections).forEach(sectionName => {
  //     Object.keys(section.customBlockFields.sections[sectionName].fields).forEach(fieldName => {
  //       ret[sectionName.concat('_', fieldName)] = this.customSections[sectionName][fieldName];
  //     });
  //   });

  //   return ret;
  // }
}

/**
 * Profile Information
 */
export class ProfileInfo extends Resource {
  firstName: string;
  lastName: string;
  nationality: string;
  country: Countries;
  birthDate: string;
  birthPlace: string;
  public load(x: any) {
    super.load(x);
    this.firstName = this.clean(x.firstName, String);
    this.lastName = this.clean(x.lastName, String);
    this.nationality = this.clean(x.nationality, String);
    this.country = x.country;
    this.birthDate = this.clean(x.birthDate, String);
    this.birthPlace = this.clean(x.birthPlace, String);
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.firstName)) e.push(`firstName`);
    if (this.iE(this.lastName)) e.push(`lastName`);
    if (this.iE(this.nationality)) e.push(`nationality`);
    if (this.iE(this.country)) e.push(`country`);
    if (this.iE(this.birthDate)) e.push(`birthDate`);
    else if (this.birthDate > new Date().toISOString()) e.push(`birthDate`);
    if (this.iE(this.birthPlace)) e.push(`birthPlace`);
    return e;
  }
}

export class ExchangeInfo extends Resource {
  originUniversity: string;
  originUniversityCity: string;
  exchangeUniversity: string;
  matriculationNumber: string;
  fieldOfStudy: FieldsOfStudy;
  public load(x: any) {
    super.load(x);
    this.originUniversity = this.clean(x.originUniversity, String);
    this.originUniversityCity = this.clean(x.originUniversityCity, String);
    this.exchangeUniversity = this.clean(x.exchangeUniversity, String);
    this.matriculationNumber = this.clean(x.matriculationNumber, String);
    this.fieldOfStudy = x.fieldOfStudy;
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.originUniversity)) e.push(`originUniversity`);
    if (this.iE(this.originUniversityCity)) e.push(`originUniversityCity`);
    if (this.iE(this.exchangeUniversity)) e.push(`exchangeUniversity`);
    if (this.iE(this.matriculationNumber)) e.push(`matriculationNumber`);
    if (this.iE(this.fieldOfStudy)) e.push(`fieldOfStudy`);
    return e;
  }
}
/**
 * Represent the contact information of the User
 */
export class Contacts extends Resource {
  phone: PhoneNumber;
  whatsapp: PhoneNumber;
  public load(x: any) {
    super.load(x);

    this.phone = new PhoneNumber(x.phone);
    this.whatsapp = new PhoneNumber(x.whatsapp);
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    e.push(...this.phone.validate({ prefix: `phone.` }));
    e.push(...this.whatsapp.validate({ prefix: `whatsapp.` }));
    return e;
  }
}

export class PhoneNumber extends Resource {
  countryPrefix: string;
  number: string;
  public load(x: any) {
    super.load(x);
    this.countryPrefix = this.clean(x.countryPrefix, String);
    this.number = this.clean(x.number, String);
  }
  public validate(options?: any): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.countryPrefix)) e.push(`${options.prefix}countryPrefix`);
    if (this.iE(this.number)) e.push(`${options.prefix}number`);
    return e;
  }
}

/**
 * Represent the Address of the User
 */
export class Address extends Resource {
  address: string;
  city: string;
  country: Countries;
  public load(x: any) {
    super.load(x);
    this.address = this.clean(x.address, String);
    this.city = this.clean(x.city, String);
    this.country = x.country;
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.address)) e.push(`address`);
    if (this.iE(this.city)) e.push(`city`);
    if (this.iE(this.country)) e.push(`country`);
    return e;
  }
}
export enum DocumentType {
  PASSPORT = 'PASSPORT',
  ID = 'ID',
  DRIVING_LICENSE = 'DRIVING_LICENSE'
}

/**
 * Represent a Identification Document
 */
export class Document extends Resource {
  type: DocumentType; //Enum: PASSPORT, ID, DRIVING_LICENSE.
  number: string;
  expirationDate: ISOString;
  issuedBy: string;
  public load(x: any) {
    super.load(x);
    this.type = this.clean(x.type, String);
    this.number = this.clean(x.number, String);
    this.expirationDate = this.clean(x.expirationDate, String);
    this.issuedBy = this.clean(x.issuedBy, String);
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.type)) e.push(`documentType`);
    if (this.iE(this.number)) e.push(`documentNumber`);
    if (this.iE(this.expirationDate)) e.push(`documentExpDate`);
    if (this.iE(this.issuedBy)) e.push(`documentIssuedBy`);
    return e;
  }
}
export class EmergencyContact extends Resource {
  name: string;
  phone: PhoneNumber;
  public load(x: any) {
    super.load(x);
    this.name = this.clean(x.name, String);
    this.phone = new PhoneNumber(x.phone);
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.name)) e.push(`emergencyContactName`);
    e.push(...this.phone.validate({ prefix: `emergencyContact.phone.` }));
    return e;
  }
}

/**
 * Represent an ESNcard issued after Association quote payment
 */
export class ESNCard extends Resource {
  number: string;
  issuedAt: ISOString;
  public load(x: any) {
    super.load(x);
    this.number = this.clean(x.number, String);
    this.issuedAt = this.clean(x.issuedAt, String);
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.number)) e.push(`esncardNumber`);
    if (this.iE(this.issuedAt)) e.push(`esncardIssuedAt`);
    return e;
  }
}

/**
 * Represents a User resource containing only fields useful for import/export operations.
 */
export interface ExternalUser {
  'First Name': string;
  'Last Name': string;
  Birthdate: string;
  Country: string;
  Nationality: string;
  Email: string;
  Phone: string;
  WhatsApp: string;
  originUniversity: string;
  originUniversityCity: string;
  exchangeUniversity: string;
  fieldOfStudy: string;
  'Creation Date': string;
  'Residence Address': string;
  'Residence City': string;
  'Residence Country': string;
  'Domicile Address': string;
  'Domicile City': string;
  'Domicile Country': string;
  'Document Type': string;
  'Document Number': string;
  'Document Expiration Date': string;
  'Document Issued In': string;
  'ESNCard Number': string;
  'ESNCard Issue Date': string;
}

/**
 * Represent the Kind of the User
 */
export enum UserKind {
  ERASMUS = 'ERASMUS',
  ESNER = 'ESNER',
  NEWBIE = 'NEWBIE',
  ALUMNO = 'ALUMNO'
}
export class Membership extends Resource {
  yearId: string;
  membershipPaymentDate: ISOString;
  transactionId: string;
  public load(x: any) {
    super.load(x);
    this.yearId = this.clean(x.yearId, String);
    this.membershipPaymentDate = this.clean(x.membershipPaymentDate, String);
    this.transactionId = this.clean(x.transactionId, String);
  }
  public validate(): string[] {
    super.validate();
    const e: string[] = [];
    if (this.iE(this.membershipPaymentDate)) e.push(`membershipPaymentDate`);
    if (this.iE(this.transactionId)) e.push(`transactionId`);
    return e;
  }
}

/**
 * Represent a PendingUser of the Application
 */
export class PendingUser extends User {
  expiresAt: epochDate; // not a string, but a number because of dynamodb Time To Live functionality
  /**
   * Load the User
   * @param user user to be loaded
   */
  load(user: any): void {
    super.load(user);
    this.expiresAt = this.clean(user.expiresAt, Number);
  }

  /**
   * Safe Load of the user
   * @param newData new data obtained
   * @param safeData safe data already present
   */
  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.expiresAt = safeData.expiresAt;
  }

  validate(): string[] {
    const e = super.validate();
    if (this.iE(this.expiresAt)) e.push('expiresAt');
    return e;
  }
}

/**
 * Represent a PendingUser of the Application
 */
export class ArchivedUser extends User {
  archivedAt: ISOString;
  /**
   * Load the User
   * @param user user to be loaded
   */
  load(user: any): void {
    super.load(user);
    this.archivedAt = this.clean(user.expiresAt, String);
  }

  /**
   * Safe Load of the user
   * @param newData new data obtained
   * @param safeData safe data already present
   */
  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
  }

  validate(): string[] {
    const e = super.validate();
    if (this.iE(this.archivedAt)) e.push('expiresAt');
    return e;
  }
}
